import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Select,
  Transfer,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CloudDownloadOutlined,
  FileExcelOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { modelingService, Modeling, Model, ModelField } from '../services/modelingService';
import { companyService } from '../services/companyService';
import { useI18n } from '../hooks/useI18n';
import { authService } from '../services/authService';

const { Text, Title } = Typography;
const { Option } = Select;

// 使用从服务导入的 Model 接口，并添加同步相关字段
interface ModelWithSync extends Model {
  sync_status?: 'synced' | 'pending' | 'error';
  last_sync_time?: string;
}

const ModelingManager: React.FC = () => {
  const { t } = useI18n();
  const [models, setModels] = useState<Model[]>([]);
  const [modelings, setModelings] = useState<Modeling[]>([]);

  // 创建带认证的 fetch 函数
  const createAuthenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('token');
    const currentTenant = localStorage.getItem('currentTenant');
    
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'X-Tenant-Code': currentTenant || 'default'
    };

    return fetch(url, {
      ...options,
      headers
    });
  };
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [form] = Form.useForm();
  const [syncLoading, setSyncLoading] = useState<number | null>(null);
  const [modelFields, setModelFields] = useState<ModelField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // 列表过滤条件
  const [filterKeyword, setFilterKeyword] = useState('');
  const [filterActiveStatus, setFilterActiveStatus] = useState('all'); // all | active | inactive
  const [filterPublishStatus, setFilterPublishStatus] = useState('all'); // all | published | unpublished
  // 建模代码筛选（按当前 tenant_code 的建模列表）
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all'); // all | project_id
  const [projectIdToCompanyName, setProjectIdToCompanyName] = useState<Record<string, string>>({});
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const obj = JSON.parse(raw);
      return !!obj?.isSuperAdmin;
    } catch {
      return false;
    }
  });

  // 获取当前 tenant 对应的 tenant_id（优先从 localStorage.user.tenants 按当前 tenant_code 匹配）
  const getCurrentTenantId = (): string => {
    const currentTenantCode = localStorage.getItem('currentTenant') || 'default';
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return String(currentTenantCode);
      const obj = JSON.parse(rawUser);
      const tenants = obj?.tenants;
      if (Array.isArray(tenants)) {
        const match = tenants.find((t: any) => t?.code === currentTenantCode || t?.tenant_code === currentTenantCode);
        const id = match?.id ?? obj?.tenant_id ?? obj?.tenantId;
        if (id !== undefined && id !== null) return String(id);
      }
      const fallbackId = obj?.tenant_id ?? obj?.tenantId;
      return fallbackId !== undefined && fallbackId !== null ? String(fallbackId) : String(currentTenantCode);
    } catch {
      return String(currentTenantCode);
    }
  };
  
  // 备份相关状态
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRemoteLoading, setBackupRemoteLoading] = useState(false);
  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [backupFilesVisible, setBackupFilesVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importInputKey, setImportInputKey] = useState(0);
  const [importModelingType, setImportModelingType] = useState<'SQLSERVER' | 'HANA'>('SQLSERVER');
  /** 导入 JSON 时必选：归属 project_id（来自公司管理） */
  const [importProjectOptions, setImportProjectOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [importProjectId, setImportProjectId] = useState<string>('');
  const [importProjectOptionsLoading, setImportProjectOptionsLoading] = useState(false);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<ModelField | null>(null);
  const [fieldForm] = Form.useForm();
  const [fieldSourceColumns, setFieldSourceColumns] = useState<Array<{ name: string; type: string }>>([]);
  const [fieldSourceColumnsLoading, setFieldSourceColumnsLoading] = useState(false);
  // 创建模型时：可用表列表（从 ChatBIAPIService 拉取），已建模型的表 disabled
  const [availableTables, setAvailableTables] = useState<Array<{ name: string; disabled: boolean }>>([]);
  const [availableTablesLoading, setAvailableTablesLoading] = useState(false);
  const [currentTableColumns, setCurrentTableColumns] = useState<Array<{ name: string; type: string }>>([]);
  const [currentTableColumnsLoading, setCurrentTableColumnsLoading] = useState(false);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>([]);
  // 向量化同步：状态与加载
  const [vectorSyncStatus, setVectorSyncStatus] = useState<string | null>(null);
  const [vectorSyncCheckLoading, setVectorSyncCheckLoading] = useState(false);
  const [vectorSyncTriggerLoading, setVectorSyncTriggerLoading] = useState(false);
  const vectorSyncPollingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // 组件卸载时清理向量化轮询
  useEffect(() => {
    return () => {
      if (vectorSyncPollingTimeoutRef.current !== null) {
        window.clearTimeout(vectorSyncPollingTimeoutRef.current);
      }
    };
  }, []);

  // 监听租户变化，重新加载数据
  useEffect(() => {
    const handleTenantChange = () => {
      loadData();
    };

    // 监听localStorage变化事件
    window.addEventListener('storage', handleTenantChange);
    
    // 监听自定义的租户变化事件
    window.addEventListener('tenantChanged', handleTenantChange);

    return () => {
      window.removeEventListener('storage', handleTenantChange);
      window.removeEventListener('tenantChanged', handleTenantChange);
    };
  }, []);

  useEffect(() => {
    if (!importVisible) return;
    let cancelled = false;
    setImportProjectOptionsLoading(true);
    (async () => {
      try {
        const tenant = localStorage.getItem('currentTenant') || 'default';
        const companyResp = await companyService.getCompanies(tenant);
        const companies = companyResp.data || [];
        const opts = companies
          .filter((c: any) => c?.project_id != null && String(c.project_id).trim() !== '')
          .map((c: any) => ({
            value: String(c.project_id),
            label: `${c.company_name || c.project_id} (${c.project_id})`.trim()
          }));
        if (cancelled) return;
        setImportProjectOptions(opts);
        setImportProjectId((prev) => {
          if (prev && opts.some((o) => o.value === prev)) return prev;
          const filterId = selectedProjectId !== 'all' ? selectedProjectId : '';
          if (filterId && opts.some((o) => o.value === filterId)) return filterId;
          return opts[0]?.value ?? '';
        });
      } catch {
        if (!cancelled) {
          setImportProjectOptions([]);
          setImportProjectId('');
        }
      } finally {
        if (!cancelled) setImportProjectOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [importVisible]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. 获取当前租户的所有激活建模
      const activeModelings = await modelingService.getActiveModelings();

      // 当前选择的 project_id 在本 tenant 下不存在时，回退到“全部”
      if (
        selectedProjectId !== 'all' &&
        !activeModelings.some(m => m.project_id === selectedProjectId)
      ) {
        setSelectedProjectId('all');
      }
      setModelings(activeModelings);

      // project_id -> 公司名（用于下拉展示）
      try {
        const tenant = localStorage.getItem('currentTenant') || 'default';
        const companyResp = await companyService.getCompanies(tenant);
        const companies = companyResp.data || [];
        const ids = Array.from(new Set(activeModelings.map(m => String(m.project_id)).filter(Boolean)));
        const map: Record<string, string> = {};
        ids.forEach((id) => {
          const matches = companies.filter(
            (c: any) => c?.project_id != null && String(c.project_id) === id
          );
          if (matches.length) {
            map[id] = matches.map((x: any) => x.company_name).filter(Boolean).join(', ');
          }
        });
        setProjectIdToCompanyName(map);
      } catch {
        setProjectIdToCompanyName({});
      }

      // 2. 根据激活的建模获取所有模型
      const allModels: Model[] = [];
      for (const modeling of activeModelings) {
        if (modeling.is_active) {
          const modelsForModeling = await modelingService.getModelsByModeling(modeling.id);
          allModels.push(...modelsForModeling);
        }
      }
      
      // 添加同步状态信息（模拟）
      const modelsWithSyncStatus = allModels.map(model => ({
        ...model,
        sync_status: Math.random() > 0.5 ? 'synced' : 'pending' as 'synced' | 'pending',
        last_sync_time: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toLocaleString('zh-CN')
      }));
      
      setModels(modelsWithSyncStatus);
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error(t('modeling.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = async () => {
    setEditingModel(null);
    form.resetFields();
    setModalVisible(true);
    setAvailableTables([]);
    setCurrentTableColumns([]);
    setSelectedColumnKeys([]);
    form.setFieldsValue({ selectedColumns: [] });
    setAvailableTablesLoading(true);
    try {
      const { tables } = await modelingService.getAvailableTablesForCreate();
      setAvailableTables(tables || []);
    } catch (e) {
      message.error(t('modeling.loadAvailableTablesFailed') || '获取可用表失败');
      setAvailableTables([]);
    } finally {
      setAvailableTablesLoading(false);
    }
  };

  const handleEditModel = async (model: Model) => {
    setEditingModel(model);
    form.setFieldsValue({
      modelName: model.model_id,
      displayName: model.display_name,
      description: model.description,
      modelingId: model.modeling_id,
      isActive: !!(model as any).is_active,
      is_synchronized: !!(model as any).is_synchronized,
      referenceName: (model as any).reference_name || model.model_id,
      sourceTableName: (model as any).source_table_name || model.model_id,
      scope: (model as any).scope || 'system'
    });
    
    // 加载模型字段
    await loadModelFields(model.id);

    // 加载该模型对应表的列信息，用于字段下拉
    const sourceTableName = (model as any).source_table_name || model.model_id;
    if (sourceTableName) {
      setFieldSourceColumnsLoading(true);
      try {
        const result = await modelingService.getTableColumns(sourceTableName);
        setFieldSourceColumns(result?.columns || []);
      } catch (e) {
        console.error('加载表字段失败:', e);
        setFieldSourceColumns([]);
      } finally {
        setFieldSourceColumnsLoading(false);
      }
    } else {
      setFieldSourceColumns([]);
    }
    
    setModalVisible(true);
  };

  const loadModelFields = async (modelId: number) => {
    setFieldsLoading(true);
    try {
      const fields = await modelingService.getModelFields(modelId);
      setModelFields(fields);
    } catch (error) {
      console.error('加载模型字段失败:', error);
      message.error(t('modeling.loadFieldFailed'));
    } finally {
      setFieldsLoading(false);
    }
  };

  const handleDeleteModel = async (id: number) => {
    try {
      await modelingService.deleteModel(id);
      setModels(models.filter(model => model.id !== id));
      message.success(t('modeling.modelDeleteSuccess'));
    } catch (error) {
      console.error('删除模型失败:', error);
      message.error(t('modeling.modelDeleteFailed'));
    }
  };

  const handleSyncModel = async (model: Model) => {
    if (syncLoading !== null) return;
    setSyncLoading(model.id);
    
    // 创建同步消息提示
    const syncMessage = message.loading(t('modeling.publishingModel', { name: model.display_name }), 0);
    
    try {
      console.log('🔄 开始同步模型:', model);
      
      // 在同步开始时，先更新本地状态为"同步中"
      setModels(prevModels => 
        prevModels.map(m => 
          m.id === model.id 
            ? { ...m, is_synchronized: false } // 设置为未同步状态，表示正在同步
            : m
        )
      );
      
      // 准备模型数据用于同步
      const modelData = {
        id: model.id,
        model_id: model.model_id,
        displayName: model.display_name,
        referenceName: model.reference_name || model.model_id,
        sourceTableName: model.source_table_name || model.model_id,
        description: model.description,
        isActive: model.is_active,
        fields: [] // 这里可以添加字段数据，如果需要的话
      };
      
      console.log('📦 准备同步的模型数据:', modelData);
      
      // 异步调用同步API，不等待结果
      const tenantId = getCurrentTenantId();
      const syncProjectId =
        modelings.find(m => m.id === model.modeling_id)?.project_id ||
        (selectedProjectId !== 'all' ? selectedProjectId : undefined);

      const syncPromise = modelingService.syncModel(model.id, modelData, {
        tenantId,
        projectId: syncProjectId,
      });
      
      // 显示同步进度提示
      message.info(t('modeling.publishStarted', { name: model.display_name }));
      
      // 等待同步完成
      const result = await syncPromise;
      
      // 关闭加载提示
      syncMessage();
      
      if (result.success) {
        console.log('✅ 同步成功:', result.message);
        message.success(result.message || t('modeling.publishSuccess', { name: model.display_name }));
        
        // 同步成功后，更新本地状态为"已同步"
        setModels(prevModels => 
          prevModels.map(m => 
            m.id === model.id 
              ? { ...m, is_synchronized: true }
              : m
          )
        );
        
        // 重新加载数据以获取最新状态（确保与数据库一致）
        await loadData();
      } else {
        console.warn('⚠️ 同步失败:', result.message);
        message.error(result.message || t('modeling.publishFailed', { name: model.display_name }));
        
        // 同步失败时，保持原状态
        await loadData();
      }
    } catch (error: any) {
      // 关闭加载提示
      syncMessage();
      
      const errorMessage = error.message || t('modeling.publishFailedGeneric');
      console.error('❌ 同步模型失败:', errorMessage, error);
      
      if (error.code === 'ECONNABORTED') {
        message.error(t('modeling.publishTimeout', { name: model.display_name }));
      } else {
        message.error(t('modeling.publishFailedWithReason', { name: model.display_name, reason: errorMessage }));
      }
      
      // 出错时，重新加载数据恢复原状态
      await loadData();
    } finally {
      setSyncLoading(null);
    }
  };

  // 备份Modeling数据
  const handleBackupModeling = async () => {
    setBackupLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`/api/${currentTenant}/modeling/backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success(t('modeling.backupSuccess', { filename: result.data.filename }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('modeling.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup modeling:', error);
      message.error(t('modeling.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // 备份远程Modeling数据
  const handleBackupRemoteModeling = async () => {
    setBackupRemoteLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`/api/${currentTenant}/modeling/backup-remote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success(t('modeling.backupRemoteSuccess', { filename: result.data.filename }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('modeling.backupRemoteFailed'));
      }
    } catch (error) {
      console.error('Failed to backup remote modeling:', error);
      message.error(t('modeling.backupRemoteFailed'));
    } finally {
      setBackupRemoteLoading(false);
    }
  };

  // 检查向量化同步状态（DeployStatus）
  const handleVectorSyncCheck = async () => {
    setVectorSyncCheckLoading(true);
    setVectorSyncStatus(null);
    try {
      const { status } = await modelingService.getVectorSyncStatus();
      setVectorSyncStatus(status);
    } catch (e) {
      message.error(t('modeling.vectorSyncCheckFailed'));
    } finally {
      setVectorSyncCheckLoading(false);
    }
  };

  // 执行向量化同步（Deploy）
  const handleVectorSyncTrigger = async () => {
    // 如果之前有轮询任务，先清理
    if (vectorSyncPollingTimeoutRef.current !== null) {
      window.clearTimeout(vectorSyncPollingTimeoutRef.current);
      vectorSyncPollingTimeoutRef.current = null;
    }

    setVectorSyncTriggerLoading(true);
    try {
      await modelingService.triggerVectorSync();
      message.success(t('modeling.vectorSyncTriggerSuccess'));
      // 先把状态设置为 deploying，然后开始轮询后台 DeployStatus
      setVectorSyncStatus('deploying');

      const maxDurationMs = 10 * 60 * 1000; // 最长 10 分钟
      const intervalMs = 5000; // 每 5 秒轮询一次
      const startTime = Date.now();

      const pollStatus = async () => {
        const elapsed = Date.now() - startTime;
        if (elapsed >= maxDurationMs) {
          vectorSyncPollingTimeoutRef.current = null;
          return;
        }

        try {
          const { status } = await modelingService.getVectorSyncStatus();
          setVectorSyncStatus(status);

          // 若仍在进行中，则继续轮询；否则停止
          if (status === 'IN_PROGRESS' || status === 'deploying') {
            vectorSyncPollingTimeoutRef.current = window.setTimeout(pollStatus, intervalMs);
          } else {
            vectorSyncPollingTimeoutRef.current = null;
          }
        } catch (e) {
          // 出错时也按照 5 秒间隔重试，直到超时
          const nowElapsed = Date.now() - startTime;
          if (nowElapsed < maxDurationMs) {
            vectorSyncPollingTimeoutRef.current = window.setTimeout(pollStatus, intervalMs);
          } else {
            vectorSyncPollingTimeoutRef.current = null;
          }
        }
      };

      // 启动第一次轮询
      pollStatus();
    } catch (e) {
      message.error(t('modeling.vectorSyncTriggerFailed'));
    } finally {
      setVectorSyncTriggerLoading(false);
    }
  };

  // 加载备份文件列表
  const loadBackupFiles = async () => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`/api/${currentTenant}/modeling/backup/files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setBackupFiles(result.data);
      }
    } catch (error) {
      console.error('Failed to load backup files:', error);
    }
  };

  // 下载备份文件
  const handleDownloadBackupFile = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`/api/${currentTenant}/modeling/backup/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      // 创建下载链接
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('modeling.fileDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error(t('modeling.fileDownloadFailed'));
    }
  };

  // 下载Excel格式备份文件
  const handleDownloadBackupFileAsExcel = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`/api/${currentTenant}/modeling/backup/${filename}/excel`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(t('modeling.downloadFailed'));
      }
      
      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let downloadFilename = filename.replace('.json', '.xlsx');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) {
          downloadFilename = decodeURIComponent(matches[1]);
        }
      }
      
      // 创建下载链接
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('modeling.excelDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download Excel file:', error);
      message.error(t('modeling.excelDownloadFailed'));
    }
  };

  // 显示备份文件列表
  const handleShowBackupFiles = () => {
    setBackupFilesVisible(true);
    loadBackupFiles();
  };

  const handleModalSubmit = async (values: any) => {
    try {
      if (editingModel) {
        // 更新模型
        await modelingService.updateModel(editingModel.id, {
          model_id: values.modelName,
          display_name: values.displayName,
          description: values.description,
          reference_name: values.referenceName || (editingModel as any).reference_name || values.modelName,
          source_table_name: values.sourceTableName || (editingModel as any).source_table_name || values.modelName,
          is_active: values.isActive,
          is_synchronized: values.is_synchronized,
          ...(isSuperAdmin ? { scope: values.scope || (editingModel as any).scope || 'system' } : {})
        });
        
        // 重新加载数据
        await loadData();
        message.success(t('modeling.modelUpdateSuccess'));
      } else {
        // 创建新模型（表与列从表单选择）
        const sourceTableName = values.sourceTableName || values.modelName;
        const newModel = await modelingService.createModel({
          model_id: values.modelName,
          display_name: values.displayName,
          reference_name: values.referenceName || values.modelName,
          source_table_name: sourceTableName,
          description: values.description,
          category: values.category,
          modeling_id: values.modelingId,
          ...(isSuperAdmin ? { scope: values.scope || 'system' } : {})
        });
        // 若选择了表列，则批量创建对应模型字段（列类型来自当前表字段信息）
        const columns: string[] = values.selectedColumns;
        if (Array.isArray(columns) && columns.length > 0 && newModel?.id) {
          const columnTypes = new Map(currentTableColumns.map((c) => [c.name, c.type]));
          const fieldsPayload = columns.map((colName: string) => ({
            field_name: colName,
            display_name: colName,
            data_type: columnTypes.get(colName) || 'string',
            description: '',
            is_calculated: false,
          }));
          await modelingService.batchCreateModelFields(newModel.id, fieldsPayload);
        }
        // 重新加载数据
        await loadData();
        message.success(t('modeling.modelCreateSuccess'));
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('操作失败:', error);
      message.error(editingModel ? t('modeling.modelUpdateFailed') : t('modeling.modelCreateFailed'));
    }
  };

  // 模型字段相关操作
  const handleCreateField = () => {
    if (!editingModel) return;
    setEditingField(null);
    fieldForm.resetFields();
    fieldForm.setFieldsValue({ model_id: editingModel.id });
    // 若还未加载表字段，尝试按当前模型的 source_table_name 重新加载
    const sourceTableName = (editingModel as any)?.source_table_name || editingModel.model_id;
    if (sourceTableName && fieldSourceColumns.length === 0) {
      setFieldSourceColumnsLoading(true);
      modelingService.getTableColumns(sourceTableName)
        .then(result => setFieldSourceColumns(result?.columns || []))
        .catch(e => {
          console.error('加载表字段失败:', e);
          setFieldSourceColumns([]);
        })
        .finally(() => setFieldSourceColumnsLoading(false));
    }
    setFieldModalVisible(true);
  };

  const handleEditField = (field: ModelField) => {
    setEditingField(field);
    fieldForm.setFieldsValue({
      field_name: field.field_name,
      display_name: field.display_name,
      description: field.description,
      data_type: field.data_type,
      is_calculated: field.is_calculated,
      is_active: field.is_active,
    });
    setFieldModalVisible(true);
  };

  const handleDeleteField = async (fieldId: number) => {
    try {
      await modelingService.deleteModelField(fieldId);
      await loadModelFields(editingModel!.id);
      message.success(t('modeling.fieldDeleteSuccess'));
    } catch (error) {
      console.error('删除字段失败:', error);
      message.error(t('modeling.fieldDeleteFailed'));
    }
  };

  const handleFieldSubmit = async (values: any) => {
    try {
      if (editingField) {
        // 更新字段
        await modelingService.updateModelField(editingField.id, {
          field_name: values.field_name,
          display_name: values.display_name,
          description: values.description,
          data_type: values.data_type,
          is_calculated: values.is_calculated,
          is_active: values.is_active,
        });
        message.success(t('modeling.fieldUpdateSuccess'));
      } else {
        // 创建新字段
        if (!editingModel) {
          message.error(t('modeling.fieldCreateFailed'));
          return;
        }
        await modelingService.createModelField({
          field_name: values.field_name,
          display_name: values.display_name,
          description: values.description,
          data_type: values.data_type,
          model_id: editingModel.id,
          is_calculated: values.is_calculated,
        });
        message.success(t('modeling.fieldCreateSuccess'));
      }
      
      // 重新加载字段列表
      await loadModelFields(editingModel!.id);
      setFieldModalVisible(false);
      fieldForm.resetFields();
    } catch (error) {
      console.error('操作失败:', error);
      message.error(editingField ? t('modeling.fieldUpdateFailed') : t('modeling.fieldCreateFailed'));
    }
  };

  const getSyncStatusTag = (status: string) => {
    switch (status) {
      case 'synced':
        return <Tag color="green" icon={<CheckCircleOutlined />}>{t('modeling.published')}</Tag>;
      case 'pending':
        return <Tag color="orange" icon={<ExclamationCircleOutlined />}>{t('modeling.pending')}</Tag>;
      case 'error':
        return <Tag color="red" icon={<ExclamationCircleOutlined />}>{t('modeling.publishError')}</Tag>;
      default:
        return <Tag>{t('modeling.unknown')}</Tag>;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'sales': return 'blue';
      case 'inventory': return 'green';
      case 'finance': return 'purple';
      case 'purchase': return 'orange';
      default: return 'default';
    }
  };

  const categoryOptions = [
    { value: 'sales', label: t('modeling.category.sales') },
    { value: 'inventory', label: t('modeling.category.inventory') },
    { value: 'finance', label: t('modeling.category.finance') },
    { value: 'purchase', label: t('modeling.category.purchase') },
    { value: 'other', label: t('modeling.category.other') },
  ];

  const dataTypeOptions = [
    { value: 'string', label: t('modeling.dataType.string') },
    { value: 'number', label: t('modeling.dataType.number') },
    { value: 'date', label: t('modeling.dataType.date') },
    { value: 'boolean', label: t('modeling.dataType.boolean') },
    { value: 'decimal', label: t('modeling.dataType.decimal') },
    { value: 'integer', label: t('modeling.dataType.integer') },
  ];

  const fieldColumns = [
    {
      title: t('modeling.field.fieldName'),
      dataIndex: 'field_name',
      key: 'field_name',
      width: 120,
      align: 'left' as const,
      render: (field_name: string) => (
        <Text code>{field_name}</Text>
      ),
    },
    {
      title: t('modeling.field.displayName'),
      dataIndex: 'display_name',
      key: 'display_name',
      width: 120,
      align: 'left' as const,
      render: (display_name: string) => (
        <Text strong>{display_name}</Text>
      ),
    },
    {
      title: t('modeling.field.description'),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      align: 'left' as const,
      render: (description: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {description || '-'}
        </Text>
      ),
    },
    {
      title: t('modeling.field.dataType'),
      dataIndex: 'data_type',
      key: 'data_type',
      width: 100,
      align: 'left' as const,
      render: (data_type: string) => (
        <Tag color="blue">{data_type}</Tag>
      ),
    },
    {
      title: t('modeling.field.isCalculated'),
      dataIndex: 'is_calculated',
      key: 'is_calculated',
      width: 100,
      align: 'left' as const,
      render: (is_calculated: boolean) => (
        <Tag color={is_calculated ? 'orange' : 'default'}>
          {is_calculated ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('modeling.field.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      align: 'left' as const,
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>
          {is_active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      align: 'left' as const,
      render: (_: any, record: ModelField) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditField(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('common.confirmDeleteField')}
            onConfirm={() => handleDeleteField(record.id)}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const modelColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'left' as const,
      sorter: (a: Model, b: Model) => a.id - b.id,
      render: (id: number) => (
        <Text code>{id}</Text>
      ),
    },
    {
      title: t('modeling.model.displayName'),
      dataIndex: 'display_name',
      key: 'display_name',
      align: 'left' as const,
      sorter: (a: Model, b: Model) => (a.display_name || '').localeCompare(b.display_name || ''),
      render: (display_name: string) => (
        <Text strong>{display_name}</Text>
      ),
    },
    {
      title: t('common.createdAt') || 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'left' as const,
      sorter: (a: any, b: any) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return ta - tb;
      },
      render: (time: string) => (time ? new Date(time).toLocaleString() : '-'),
    },
    {
      title: t('modeling.model.lastPublishTime'),
      dataIndex: 'last_sync_time',
      key: 'last_sync_time',
      align: 'left' as const,
      sorter: (a: any, b: any) => {
        const ta = a.last_sync_time ? new Date(a.last_sync_time).getTime() : 0;
        const tb = b.last_sync_time ? new Date(b.last_sync_time).getTime() : 0;
        return ta - tb;
      },
      render: (time: string) => time || '-',
    },
    {
      title: t('modeling.model.activeStatus'),
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'left' as const,
      sorter: (a: any, b: any) => Number(!!a.is_active) - Number(!!b.is_active),
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>
          {is_active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('modeling.model.publishStatus'),
      dataIndex: 'is_synchronized',
      key: 'is_synchronized',
      align: 'left' as const,
      sorter: (a: any, b: any) => Number(!!a.is_synchronized) - Number(!!b.is_synchronized),
      render: (is_synchronized: boolean, record: Model) => {
        // 如果正在同步中，显示"同步中"状态
        if (syncLoading === record.id) {
          return (
            <Tag color="blue" icon={<SyncOutlined spin />}>
              {t('modeling.publishing')}
            </Tag>
          );
        }
        
        // 否则显示正常的同步状态
        return (
          <Tag color={is_synchronized ? 'green' : 'orange'}>
            {is_synchronized ? t('modeling.published') : t('modeling.unpublished')}
          </Tag>
        );
      },
    },
    ...(isSuperAdmin ? [{
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      align: 'left' as const,
      sorter: (a: any, b: any) => (a.scope || 'system').localeCompare(b.scope || 'system'),
      render: (scope: string) => (
        <Tag color={scope === 'system' ? 'blue' : 'green'}>
          {scope || 'system'}
        </Tag>
      ),
    }] : []),
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'left' as const,
      render: (_: any, record: Model) => (
        <Space>
          <Tooltip title={t('modeling.publishModel')}>
            <Button
              type="primary"
              size="small"
              icon={<SyncOutlined />}
              loading={syncLoading === record.id}
              onClick={() => handleSyncModel(record)}
            >
              {t('modeling.publish')}
            </Button>
          </Tooltip>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditModel(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('modeling.confirmDeleteModel')}
            onConfirm={() => handleDeleteModel(record.id)}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeModels = models.filter(model => model.is_active);
  const syncedModels = models.filter(model => model.is_synchronized);
  const pendingModels = models.filter(model => !model.is_synchronized);

  const toVersionParts = (v: string) =>
    (v || '')
      .toString()
      .split(/[^0-9]+/)
      .filter(Boolean)
      .map(x => Number.parseInt(x, 10));

  const compareVersion = (a: string, b: string) => {
    const pa = toVersionParts(a);
    const pb = toVersionParts(b);
    if (!pa.length && !pb.length) return (a || '').localeCompare(b || '');
    if (!pa.length) return -1;
    if (!pb.length) return 1;
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
      const na = pa[i] ?? 0;
      const nb = pb[i] ?? 0;
      if (na !== nb) return na - nb;
    }
    return 0;
  };

  // 同一个 project_id 只保留最新 version 的那条记录
  const modelingByProjectId = (() => {
    const map = new Map<string, Modeling>();
    for (const m of modelings) {
      if (!m.project_id) continue;
      const existing = map.get(m.project_id);
      if (!existing || compareVersion(m.version, existing.version) > 0) {
        map.set(m.project_id, m);
      }
    }
    return map;
  })();

  const selectedModelingId =
    selectedProjectId === 'all' ? null : modelingByProjectId.get(selectedProjectId)?.id ?? null;

  const latestModelingVersion = (() => {
    const target =
      selectedProjectId === 'all'
        ? Array.from(modelingByProjectId.values())
        : modelingByProjectId.get(selectedProjectId)
          ? [modelingByProjectId.get(selectedProjectId)!]
          : [];

    if (!target.length) return '-';
    const best = target.reduce((acc, cur) =>
      compareVersion(cur.version ?? '', acc.version ?? '') > 0 ? cur : acc
    );
    return best.version || '-';
  })();

  // 根据过滤条件筛选模型列表
  const filteredModels = models.filter((model) => {
    const keyword = filterKeyword.trim().toLowerCase();
    const keywordMatch =
      !keyword ||
      (model.display_name && model.display_name.toLowerCase().includes(keyword)) ||
      (model.model_id && model.model_id.toLowerCase().includes(keyword)) ||
      ((model as any).source_table_name &&
        (model as any).source_table_name.toLowerCase().includes(keyword)) ||
      ((model as any).reference_name &&
        (model as any).reference_name.toLowerCase().includes(keyword));

    const activeMatch =
      filterActiveStatus === 'all'
        ? true
        : filterActiveStatus === 'active'
          ? model.is_active
          : !model.is_active;

    const publishMatch =
      filterPublishStatus === 'all'
        ? true
        : filterPublishStatus === 'published'
          ? !!model.is_synchronized
          : !model.is_synchronized;

    const modelingMatch = selectedModelingId ? model.modeling_id === selectedModelingId : true;

    return keywordMatch && activeMatch && publishMatch && modelingMatch;
  });

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题和操作按钮 */}
      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <DatabaseOutlined style={{ marginRight: '8px' }} />
              {t('modeling.title')}
            </Title>
            <Select
              style={{ width: 240 }}
              value={selectedProjectId}
              onChange={(value) => setSelectedProjectId(value)}
              disabled={modelings.length === 0}
              options={[
                { value: 'all', label: t('common.all') },
                  ...Array.from(modelingByProjectId.values()).map(m => ({
                    value: m.project_id,
                    label: projectIdToCompanyName[m.project_id] || m.project_id,
                  })),
              ]}
            />
            <Text type="secondary">
              Version: {latestModelingVersion}
            </Text>
            <Text type="secondary">{t('modeling.subtitle')}</Text>

            <Space style={{ flexWrap: 'wrap' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateModel}
              >
                {t('modeling.createModel')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupModeling}
              >
                {t('modeling.backupLocal')}
              </Button>
              <Button
                icon={<CloudDownloadOutlined />}
                loading={backupRemoteLoading}
                onClick={handleBackupRemoteModeling}
              >
                {t('modeling.backupRemote')}
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportVisible(true)}
              >
                {t('common.import')}
              </Button>
              {isSuperAdmin && (
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleShowBackupFiles}
                >
                  {t('modeling.backupFiles')}
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={loadData}
                loading={loading}
              >
                {t('common.refresh')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      {/* 顶部统计 + 向量化同步状态（两个等高 Card） */}
      <Row gutter={16} align="stretch" style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card
            style={{
              height: '100%',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Space size={48} align="center">
              <Statistic
                title={t('modeling.stat.totalModels')}
                value={models.length}
                prefix={<DatabaseOutlined />}
              />
              <Statistic
                title={t('modeling.stat.activeModels')}
                value={activeModels.length}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Statistic
                title={t('modeling.stat.published')}
                value={syncedModels.length}
                valueStyle={{ color: '#1890ff' }}
                prefix={<SyncOutlined />}
              />
              <Statistic
                title={t('modeling.stat.pending')}
                value={pendingModels.length}
                valueStyle={{ color: '#faad14' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Space>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={t('modeling.vectorSync')}
            style={{
              height: '100%',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Space direction="vertical" size="small" align="center">
              <Space>
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  loading={vectorSyncCheckLoading}
                  onClick={handleVectorSyncCheck}
                >
                  {t('modeling.vectorSyncCheck')}
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={vectorSyncTriggerLoading}
                  disabled={vectorSyncStatus === 'SYNCRONIZED'}
                  onClick={handleVectorSyncTrigger}
                >
                  {t('modeling.vectorSyncTrigger')}
                </Button>
              </Space>
              <Text type="secondary">
                {t('modeling.vectorSyncStatus')}:{' '}
                {vectorSyncStatus !== null ? (
                  <Tag color="blue">{vectorSyncStatus}</Tag>
                ) : (
                  <span>{t('modeling.vectorSyncStatusUnknown')}</span>
                )}
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 模型列表 */}
      <Card>
        {/* 过滤条件 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            style={{ width: 260 }}
            placeholder={t('modeling.filters.keywordPlaceholder')}
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
          />
          <Select
            style={{ width: 180 }}
            value={filterActiveStatus}
            onChange={(value) => setFilterActiveStatus(value)}
          >
            <Option value="all">{t('modeling.filters.activeAll')}</Option>
            <Option value="active">{t('modeling.filters.activeOnly')}</Option>
            <Option value="inactive">{t('modeling.filters.inactiveOnly')}</Option>
          </Select>
          <Select
            style={{ width: 200 }}
            value={filterPublishStatus}
            onChange={(value) => setFilterPublishStatus(value)}
          >
            <Option value="all">{t('modeling.filters.publishAll')}</Option>
            <Option value="published">{t('modeling.filters.publishedOnly')}</Option>
            <Option value="unpublished">{t('modeling.filters.unpublishedOnly')}</Option>
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilterKeyword('');
              setFilterActiveStatus('all');
              setFilterPublishStatus('all');
            }}
          >
            {t('common.reset')}
          </Button>
        </Space>

        {/* 导入弹窗 */}
        <Modal
          title={t('common.import')}
          open={importVisible}
          onCancel={() => { setImportVisible(false); setImportFile(null); }}
          footer={null}
          width={600}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <span style={{ marginRight: 8 }}>{t('modeling.importSelectProject')}:</span>
              <Select
                showSearch
                optionFilterProp="label"
                loading={importProjectOptionsLoading}
                placeholder={t('modeling.importSelectProjectPlaceholder')}
                value={importProjectId || undefined}
                style={{ width: '100%', maxWidth: 420 }}
                onChange={(v) => setImportProjectId(v)}
                options={importProjectOptions}
              />
              {!importProjectOptionsLoading && importProjectOptions.length === 0 && (
                <div style={{ color: '#faad14', fontSize: 12, marginTop: 4 }}>{t('modeling.importNoProjectOptions')}</div>
              )}
            </div>
            <div>
              <span style={{ marginRight: 8 }}>{t('modeling.modelingType')}:</span>
              <Select
                value={importModelingType}
                style={{ width: 200 }}
                onChange={(val) => setImportModelingType(val as 'SQLSERVER' | 'HANA')}
                options={[
                  { value: 'SQLSERVER', label: 'SQLSERVER' },
                  { value: 'HANA', label: 'HANA' }
                ]}
              />
            </div>
            <input
              type="file"
              accept="application/json,.json"
              key={importInputKey}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setImportFile(f);
              }}
            />
            <div style={{ color: '#999' }}>
              {t('modeling.importFileTip')}
            </div>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => { setImportVisible(false); setImportFile(null); setImportInputKey(k => k + 1); }}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="default"
                  loading={importing}
                  disabled={!importProjectId || importProjectOptionsLoading}
                  onClick={async () => {
                  if (!importProjectId) {
                    message.warning(t('modeling.importProjectRequired'));
                    return;
                  }
                  try {
                    setImporting(true);
                    const res = await modelingService.importModeling({
                      projectId: importProjectId,
                      modelingType: importModelingType
                    });
                    if (res.success) {
                      message.success(res.message || t('modeling.modelUpdateSuccess'));
                      await loadData();
                      setImportVisible(false);
                      setImportFile(null);
                      setImportInputKey(k => k + 1);
                    } else {
                      message.error(res.message || t('modeling.modelUpdateFailed'));
                    }
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || e?.message || t('modeling.importFailed'));
                  } finally {
                    setImporting(false);
                  }
                }}>
                  {t('common.import')} ({t('modeling.importDefault')})
                </Button>
                <Button
                  type="primary"
                  loading={importing}
                  disabled={!importProjectId || importProjectOptionsLoading}
                  onClick={async () => {
                  if (!importProjectId) {
                    message.warning(t('modeling.importProjectRequired'));
                    return;
                  }
                  if (!importFile) { message.info(t('modeling.noFileSelectedUseDefault')); return; }
                  setImporting(true);
                  try {
                    // 上传文件到后端不需要，后端读取 filePath；这里使用 FileReader 不可靠，改用上传到临时后端并传 modeling/models 也可。
                    // 简化：读取内容并直接传 models 给后端（后端已支持 modeling/models 直传）
                    const text = await importFile.text();
                    const json = JSON.parse(text);
                    let payload: any = {
                      projectId: importProjectId
                    };
                    if (json?.modeling) payload.modeling = json.modeling;
                    else if (Array.isArray(json?.modelings)) payload.modeling = (json.modelings.find((m: any) => m.is_active) || json.modelings[0]);
                    else if (Array.isArray(json?.models)) payload.models = json.models;
                    else payload.models = Array.isArray(json) ? json : [];

                    payload.modelingType = importModelingType;

                    const res = await modelingService.importModeling(payload);
                    if (res.success) {
                      message.success(res.message || t('modeling.modelUpdateSuccess'));
                      await loadData();
                      setImportVisible(false);
                      setImportFile(null);
                      setImportInputKey(k => k + 1);
                    } else {
                      message.error(res.message || t('modeling.modelUpdateFailed'));
                    }
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || e?.message || t('modeling.importFailed'));
                  } finally {
                    setImporting(false);
                  }
                }}>
                  {t('common.import')} ({t('common.file')})
                </Button>
              </Space>
            </div>
          </Space>
        </Modal>
        <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          <Popconfirm
            title={t('modeling.confirmBatchDelete')}
            onConfirm={async () => {
              if (selectedRowKeys.length === 0) return;
              setBulkDeleting(true);
              try {
                for (const id of selectedRowKeys as number[]) {
                  await modelingService.deleteModel(id);
                }
                message.success(t('modeling.batchDeleteSuccess', { count: selectedRowKeys.length }));
                setSelectedRowKeys([]);
                await loadData();
              } catch (e) {
                message.error(t('modeling.batchDeleteFailed'));
              } finally {
                setBulkDeleting(false);
              }
            }}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
            disabled={selectedRowKeys.length === 0}
          >
            <Button danger loading={bulkDeleting} disabled={selectedRowKeys.length === 0}>
              {t('modeling.batchDelete')}
            </Button>
          </Popconfirm>
          <Button
            type="primary"
            loading={bulkSyncing}
            disabled={selectedRowKeys.length === 0}
            onClick={async () => {
              if (selectedRowKeys.length === 0) return;
              if (bulkSyncing) return; // 防止用户重复点击导致流程被打断
              setBulkSyncing(true);
              try {
                const tenantId = getCurrentTenantId();
                const target = models.filter(m => selectedRowKeys.includes(m.id));
                let ok = 0, failed = 0;
                for (const m of target) {
                  const modelData = {
                    id: m.id,
                    model_id: m.model_id,
                    displayName: m.display_name,
                    referenceName: m.reference_name || m.model_id,
                    sourceTableName: m.source_table_name || m.model_id,
                    description: m.description,
                    isActive: m.is_active,
                    fields: []
                  };
                  const syncProjectId =
                    modelings.find(x => x.id === m.modeling_id)?.project_id ||
                    (selectedProjectId !== 'all' ? selectedProjectId : undefined);

                  const result = await modelingService.syncModel(m.id, modelData, {
                    tenantId,
                    projectId: syncProjectId,
                  });
                  if (result.success) ok++; else failed++;
                }
                message.success(t('modeling.batchPublishResult', { ok, failed }));
                await loadData();
              } catch (e) {
                message.error(t('modeling.batchPublishFailed'));
              } finally {
                setBulkSyncing(false);
              }
            }}
          >
            {t('modeling.batchPublish')}
          </Button>
        </div>
        <Table
          columns={modelColumns}
          dataSource={filteredModels}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('common.paginationTotal', { from: range[0], to: range[1], total }),
          }}
        />
      </Card>

      {/* 创建/编辑模型模态框 */}
      <Modal
        title={editingModel ? t('modeling.modal.editTitle') : t('modeling.modal.createTitle')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setModelFields([]);
        }}
        footer={null}
        width={editingModel ? 1040 : 800}
        centered
        style={{ top: '2vh' }}
        bodyStyle={editingModel ? { maxHeight: '90vh', overflowY: 'hidden' } : undefined}
      >
        {editingModel ? (
          <div>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleModalSubmit}
            >
                           {/* 第一行：模型名称、显示名称、状态、同步状态 */}
            <Row gutter={16}>
               <Col span={6}>
                 <Form.Item
                   name="modelName"
                   label={t('modeling.modelName')}
                   rules={[{ required: true, message: t('modeling.enterModelName') }]}
                 >
                   <Input placeholder={t('modeling.enterModelName')} />
                 </Form.Item>
               </Col>
               <Col span={6}>
                 <Form.Item
                   name="displayName"
                   label={t('modeling.displayName')}
                   rules={[{ required: true, message: t('modeling.enterDisplayName') }]}
                 >
                   <Input placeholder={t('modeling.enterDisplayName')} />
                 </Form.Item>
               </Col>
               <Col span={6}>
                 <Form.Item
                   name="isActive"
                   label={t('modeling.model.activeStatus')}
                 >
                   <Select placeholder={t('modeling.selectActiveStatus')}>
                     <Option value={true}>{t('common.active')}</Option>
                     <Option value={false}>{t('common.inactive')}</Option>
                   </Select>
                 </Form.Item>
               </Col>
               <Col span={6}>
                 <Form.Item
                   name="is_synchronized"
                   label={t('modeling.model.publishStatus')}
                 >
                   <Select placeholder={t('modeling.selectPublishStatus')}>
                     <Option value={true}>{t('modeling.published')}</Option>
                     <Option value={false}>{t('modeling.unpublished')}</Option>
                   </Select>
                 </Form.Item>
               </Col>
             </Row>

              {/* 第二行：描述 */}
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="description"
                    label={t('modeling.description')}
                  >
                    <Input.TextArea rows={2} placeholder={t('modeling.enterModelDescription')} />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第三行：scope（仅超级管理员）、reference_name、source_table_name 同一行 */}
              <Row gutter={16}>
                {isSuperAdmin && (
                  <Col span={8}>
                    <Form.Item
                      name="scope"
                      label={t('modeling.scope') || 'Scope'}
                      tooltip={t('modeling.scopeTip')}
                    >
                      <Select placeholder={t('modeling.selectScope')}>
                        <Option value="system">system</Option>
                        <Option value="tenant">tenant</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col span={isSuperAdmin ? 8 : 12}>
                  <Form.Item
                    name="referenceName"
                    label={t('modeling.referenceName')}
                    rules={[{ required: true, message: t('modeling.enterReferenceName') }]}
                  >
                    <Input placeholder={t('modeling.enterReferenceName')} />
                  </Form.Item>
                </Col>
                <Col span={isSuperAdmin ? 8 : 12}>
                  <Form.Item
                    name="sourceTableName"
                    label={t('modeling.sourceTableName')}
                    rules={[{ required: true, message: t('modeling.enterSourceTableName') }]}
                  >
                    <Input placeholder={t('modeling.enterSourceTableName')} />
                  </Form.Item>
                </Col>
              </Row>

            </Form>

            {/* 字段管理区域 */}
            <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5} style={{ margin: 0 }}>{t('modeling.field.title')}</Title>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleCreateField}
                >
                  {t('modeling.field.addField')}
                </Button>
              </div>
              <Table
                columns={fieldColumns}
                dataSource={modelFields}
                rowKey="id"
                loading={fieldsLoading}
                size="small"
                pagination={{ pageSize: 6 }}
              />
            </div>

                       {/* 操作按钮区域 */}
           <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '16px', textAlign: 'right' }}>
             <Space>
               <Button onClick={() => setModalVisible(false)}>
                 {t('common.cancel')}
               </Button>
               <Tooltip title={t('modeling.publishModel')}>
                 <Button 
                   type="default"
                   icon={<SyncOutlined />}
                   loading={syncLoading === editingModel?.id}
                   onClick={() => editingModel && handleSyncModel(editingModel)}
                 >
                   {t('modeling.publish')}
                 </Button>
               </Tooltip>
               <Button type="primary" onClick={() => form.submit()}>
                 {t('common.update')}
               </Button>
             </Space>
           </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleModalSubmit}
          >
            <Form.Item
              name="sourceTableName"
              label={t('modeling.sourceTableName')}
              rules={[{ required: true, message: t('modeling.enterSourceTableName') || '请选择表' }]}
            >
              <Select
                placeholder={t('modeling.selectTable') || '请选择数据源表'}
                showSearch
                allowClear
                loading={availableTablesLoading}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                onChange={async (name) => {
                  setCurrentTableColumns([]);
                  setSelectedColumnKeys([]);
                  form.setFieldsValue({ selectedColumns: [] });
                  if (!name) {
                    form.setFieldsValue({ modelName: undefined, displayName: undefined });
                    return;
                  }
                  // 选表后始终将 Model Name / Display Name 设置为表名
                  form.setFieldsValue({ modelName: name, displayName: name });
                  setCurrentTableColumnsLoading(true);
                  try {
                    const result = await modelingService.getTableColumns(name);
                    const columns = result?.columns || [];
                    setCurrentTableColumns(columns);
                    // 默认不选中任何列，由用户自行选择
                    setSelectedColumnKeys([]);
                    form.setFieldsValue({ selectedColumns: [] });
                  } catch (e) {
                    message.error(t('modeling.loadTableColumnsFailed') || '获取表字段失败');
                    setCurrentTableColumns([]);
                  } finally {
                    setCurrentTableColumnsLoading(false);
                  }
                }}
                options={availableTables.map((table) => ({
                  value: table.name,
                  label: table.disabled ? `${table.name} (${t('modeling.modelAlreadyCreated')})` : table.name,
                  disabled: table.disabled
                }))}
                optionRender={({ data }: { data: { label: string; disabled?: boolean } }) => (
                  <span style={{ color: data.disabled ? '#999' : undefined }}>{data.label}</span>
                )}
              />
            </Form.Item>

            <Form.Item
              name="selectedColumns"
              label={t('modeling.selectColumns')}
            >
              <Transfer
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                dataSource={currentTableColumns.map((c) => ({
                  key: c.name,
                  title: `${c.name} (${c.type})`,
                }))}
                showSearch
                targetKeys={selectedColumnKeys}
                onChange={(nextTargetKeys) => {
                  const keys = nextTargetKeys as string[];
                  setSelectedColumnKeys(keys);
                  form.setFieldsValue({ selectedColumns: keys });
                }}
                render={(item) => item.title || ''}
                listStyle={{ width: '47%', height: 280 }}
                titles={[t('modeling.availableColumns') || '可选列', t('modeling.selectedColumnsTitle') || '已选列']}
                locale={{
                  itemUnit: t('common.items') || '项',
                  itemsUnit: t('common.items') || '项',
                  notFoundContent: t('common.noData'),
                  searchPlaceholder: t('common.search'),
                }}
                disabled={!currentTableColumns.length}
              />
            </Form.Item>

            {/* 第一行：Model Name & Display Name */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="modelName"
                  label={t('modeling.modelName')}
                  rules={[{ required: true, message: t('modeling.enterModelName') }]}
                >
                  <Input placeholder={t('modeling.enterModelName')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="displayName"
                  label={t('modeling.displayName')}
                  rules={[{ required: true, message: t('modeling.enterDisplayName') }]}
                >
                  <Input placeholder={t('modeling.enterDisplayName')} />
                </Form.Item>
              </Col>
            </Row>

            {/* 第二行：Category & Modeling */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label={t('modeling.category.title')}
                  rules={[{ required: true, message: t('modeling.selectCategory') }]}
                >
                  <Select placeholder={t('modeling.selectCategory')}>
                    {categoryOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="modelingId"
                  label={t('modeling.modelingBelong')}
                  rules={[{ required: true, message: t('modeling.selectModeling') }]}
                >
                  <Select placeholder={t('modeling.selectModeling')}>
                    {modelings.filter(m => m.is_active).map(modeling => (
                      <Option key={modeling.id} value={modeling.id}>
                        {modeling.display_name || modeling.modeling_name} ({projectIdToCompanyName[modeling.project_id] || modeling.project_id})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* 描述放在最下面 */}
            <Form.Item
              name="description"
              label={t('modeling.description')}
            >
              <Input.TextArea rows={3} placeholder={t('modeling.enterModelDescription')} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setModalVisible(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {t('common.create')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* 创建/编辑模型字段模态框 */}
      <Modal
        title={editingField ? t('modeling.field.modal.editTitle') : t('modeling.field.modal.createTitle')}
        open={fieldModalVisible}
        onCancel={() => {
          setFieldModalVisible(false);
          fieldForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={fieldForm}
          layout="vertical"
          onFinish={handleFieldSubmit}
        >
          <Form.Item
            name="field_name"
            label={t('modeling.field.fieldName')}
            rules={[{ required: true, message: t('modeling.field.enterFieldName') }]}
          >
            {fieldSourceColumns.length > 0 ? (
              <Select
                showSearch
                allowClear={!editingField}
                placeholder={t('modeling.field.enterFieldName')}
                loading={fieldSourceColumnsLoading}
                optionFilterProp="label"
                onChange={(val) => {
                  if (!val) return;
                  const col = fieldSourceColumns.find((c) => c.name === val);
                  if (col) {
                    // 默认 display_name 与字段名相同
                    fieldForm.setFieldsValue({
                      display_name: col.name,
                      data_type: (col.type || '').toLowerCase().includes('int')
                        ? 'integer'
                        : (col.type || '').toLowerCase().includes('dec') || (col.type || '').toLowerCase().includes('numeric')
                          ? 'decimal'
                          : (col.type || '').toLowerCase().includes('date') || (col.type || '').toLowerCase().includes('time')
                            ? 'date'
                            : (col.type || '').toLowerCase().includes('bit') || (col.type || '').toLowerCase().includes('bool')
                              ? 'boolean'
                              : 'string'
                    });
                  }
                }}
                options={fieldSourceColumns.map((col) => {
                  const exists = modelFields.some(
                    (f) => f.field_name === col.name && (!editingField || editingField.field_name !== col.name)
                  );
                  return {
                    value: col.name,
                    label: exists
                      ? `${col.name} (${col.type}) - ${t('modeling.field.alreadyExists') || '已存在'}`
                      : `${col.name} (${col.type})`,
                    disabled: exists,
                  };
                })}
                optionRender={({ data }: { data: { label: string; disabled?: boolean } }) => (
                  <span style={{ color: data.disabled ? '#999' : undefined }}>{data.label}</span>
                )}
              />
            ) : (
              <Input placeholder={t('modeling.field.enterFieldName')} />
            )}
          </Form.Item>

          <Form.Item
            name="display_name"
            label={t('modeling.field.displayName')}
            rules={[{ required: true, message: t('modeling.field.enterDisplayName') }]}
          >
            <Input placeholder={t('modeling.field.enterDisplayName')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('modeling.field.description')}
          >
            <Input.TextArea rows={2} placeholder={t('modeling.enterFieldDescription')} />
          </Form.Item>

          <Form.Item
            name="data_type"
            label={t('modeling.field.dataType')}
            rules={[{ required: true, message: t('modeling.field.selectDataType') }]}
          >
            <Select placeholder={t('modeling.field.selectDataType')}>
              {dataTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_calculated"
            label={t('modeling.field.isCalculated')}
            valuePropName="checked"
          >
            <Select placeholder={t('modeling.field.selectIsCalculated')}>
              <Option value={false}>{t('common.no')}</Option>
              <Option value={true}>{t('common.yes')}</Option>
            </Select>
          </Form.Item>

          {editingField && (
            <Form.Item
              name="is_active"
              label={t('modeling.field.status')}
            >
              <Select placeholder={t('modeling.field.selectStatus')}>
                <Option value={true}>{t('common.active')}</Option>
                <Option value={false}>{t('common.inactive')}</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setFieldModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingField ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 备份文件列表模态框（仅超级管理员） */}
      {isSuperAdmin && (
        <Modal
          title={t('modeling.backupFileList')}
          open={backupFilesVisible}
          onCancel={() => setBackupFilesVisible(false)}
          footer={[
            <Button key="close" onClick={() => setBackupFilesVisible(false)}>
              {t('common.close')}
            </Button>
          ]}
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('modeling.backupFilesListDescription')}</Text>
          </div>

          {backupFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>{t('modeling.noBackupFiles')}</div>
            </div>
          ) : (
            <Table
              dataSource={backupFiles}
              rowKey="filename"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t('modeling.backupFile.filename'),
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (filename: string) => (
                    <Space>
                      <FileTextOutlined />
                      <Text code>{filename}</Text>
                    </Space>
                  ),
                },
                {
                  title: t('modeling.backupFile.size'),
                  dataIndex: 'size',
                  key: 'size',
                  render: (size: number) => (
                    <Text type="secondary">{(size / 1024).toFixed(1)} KB</Text>
                  ),
                },
                {
                  title: t('modeling.backupFile.created'),
                  dataIndex: 'created',
                  key: 'created',
                  render: (created: string) => (
                    <Text type="secondary">
                      {new Date(created).toLocaleString('zh-CN')}
                    </Text>
                  ),
                },
                {
                  title: t('modeling.backupFile.actions'),
                  key: 'actions',
                  render: (_: unknown, record: any) => (
                    <Space>
                      <Tooltip title={t('modeling.downloadJson')}>
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadBackupFile(record.filename)}
                        >
                          {t('modeling.json')}
                        </Button>
                      </Tooltip>
                      <Tooltip title={t('modeling.downloadExcel')}>
                        <Button
                          type="link"
                          icon={<FileExcelOutlined />}
                          onClick={() => handleDownloadBackupFileAsExcel(record.filename)}
                        >
                          {t('modeling.excel')}
                        </Button>
                      </Tooltip>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Modal>
      )}

      {/* 删除本地文件导入改为后端默认导入。保留占位，不再显示弹窗 */}
    </div>
  );
};

export default ModelingManager;
